import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import logo from "../../../../../assets/images/logo.png";
import { createPatientMaleChartAction } from "../../../../../store/actions/PatientMalechartAction";
import { handleSuccess, showErrorAlert } from "../../../../../services/ErrorHandlingService";
import FormSuccessMessage from "../../../Common/FormSuccessMessage";

const MaleFormPage = () => {
  const dispatch = useDispatch();
  const { patientId } = useParams(); // Base64 from URL

  // Decode Base64 patient ID safely
  let decodedPatientId = null;
  try {
    decodedPatientId = patientId ? atob(patientId) : null;
  } catch (err) {
    console.error("Invalid patient ID in URL:", err);
  }

  console.log("Decoded Patient ID:", decodedPatientId);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    date: "",
    symptoms: null,
    otherSymptom: "",
    metallicColor: null,
    by_patient:"YES"
  });

  const symptomsList = [
    "Decreased duration of intercourse",
    "Reduced erection hardness",
    "Semen has become watery",
    "Decrease in semen volume",
    "Lack of sexual desire",
    "Increased fatigue",
    "Body aches",
    "Burning sensation or heat in the genital area",
    "Lumps felt in semen",
    "Blood present in semen",
    "Foul or decayed smell from semen",
    "Pus discharge in semen",
    "Pain during or after intercourse",
    "Burning sensation in the genitals",
    "Rashes or eruptions on the genitals",
    "Cracks or fissures on the genitals",
    "None of the above",
    "Other",
  ];

  const metallicColorOptions = [
    { value: "White", label: "White" },
    { value: "Gray", label: "Gray" },
    { value: "Yellow", label: "Yellow" },
  ];

  const symptomsOptions = symptomsList.map((symptom) => ({
    value: symptom,
    label: symptom,
  }));

  // Handle normal input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle react-select
  const handleSelectChange = (selectedOption, { name }) => {
    setFormData((prev) => ({ ...prev, [name]: selectedOption }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.date.trim()) newErrors.date = "Date is required";

    if (!formData.symptoms || formData.symptoms.length === 0)
      newErrors.symptoms = "At least one symptom is required";

    if (!formData.metallicColor) newErrors.metallicColor = "Metallic color is required";

    if (
      formData.symptoms?.some((option) => option.value === "Other") &&
      !formData.otherSymptom.trim()
    ) {
      newErrors.otherSymptom = "Please specify the other symptom";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
  
    if (!decodedPatientId) {
      showErrorAlert("Missing patient ID", "error");
      return;
    }
  
    if (!validateForm()) return;
  
    setIsSubmitting(true);
  
    try {
      const payload = {
        patient_id: decodedPatientId, // send decoded value to backend
        date: formData.date,
        symptoms: formData.symptoms,
        otherSymptom: formData.otherSymptom,
        metallicColor: formData.metallicColor,
        by_patient: formData.by_patient, 
      };
  
      const result = await dispatch(createPatientMaleChartAction(payload));
  
      if (result.success) {
        setIsSubmitted(true);  // ✅ Switch to Thank You page
        handleSuccess("Male chart created successfully!", "success");
  
        // optional: reset form (not needed if we show thank-you instead)
        setFormData({
          date: "",
          symptoms: null,
          otherSymptom: "",
          metallicColor: null,
        });
        setErrors({});
      }
    } catch (err) {
      console.error("Submit error:", err);
      const message = err?.response?.data?.message || "Failed to submit form";
      showErrorAlert(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isSubmitted) {
    return (
      <FormSuccessMessage
        title="Form submitted successfully. Thank you!"
        message="Your response has been recorded successfully."
      />
    );
  }
  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="text-center mb-4">
          <img src={logo} alt="Logo" style={{ width: "200px", height: "auto" }} />
          {/* <h2 className="mt-2">Male Chart Form</h2> */}
        </div>

        {!decodedPatientId ? (
          <div className="alert alert-danger">Missing or invalid patient ID</div>
        ) : (
          <div className="col-md-8 col-lg-6">
            <form className="card p-4 shadow" onSubmit={handleSubmit}>
              <div className="text-center mb-4">
                <h2 className="mt-2">Male Chart Form</h2>
              </div>
              {/* Date */}
              <div className="mb-3">
                <label className="form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
                {errors.date && <small className="text-danger">{errors.date}</small>}
              </div>

              {/* Symptoms */}
              <div className="mb-3">
                <label className="form-label">
                  Symptoms during/after intercourse? <span className="text-danger">*</span>
                </label>
                <Select
                  isMulti
                  name="symptoms"
                  value={formData.symptoms}
                  onChange={handleSelectChange}
                  options={symptomsOptions}
                  placeholder="Select symptoms"
                  isClearable
                  styles={{
                    control: (provided) => ({ ...provided, minHeight: "50px" }),
                    dropdownIndicator: (provided) => ({ ...provided, padding: "4px" }),
                    indicatorSeparator: () => ({ display: "none" }),
                  }}
                />
                {errors.symptoms && <small className="text-danger">{errors.symptoms}</small>}

                {formData.symptoms?.some((option) => option.value === "Other") && (
                  <div className="mt-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Please specify other symptom"
                      name="otherSymptom"
                      value={formData.otherSymptom}
                      onChange={handleChange}
                    />
                    {errors.otherSymptom && (
                      <small className="text-danger">{errors.otherSymptom}</small>
                    )}
                  </div>
                )}
              </div>

              {/* Metallic Color */}
              <div className="mb-3">
                <label className="form-label">
                  Metallic Color <span className="text-danger">*</span>
                </label>
                <Select
                  name="metallicColor"
                  value={formData.metallicColor}
                  onChange={(selected, action) =>
                    handleSelectChange(selected, { ...action, name: "metallicColor" })
                  }
                  options={metallicColorOptions}
                  placeholder="Select color"
                  isClearable
                  styles={{
                    control: (provided) => ({ ...provided, minHeight: "50px" }),
                    dropdownIndicator: (provided) => ({ ...provided, padding: "4px" }),
                    indicatorSeparator: () => ({ display: "none" }),
                  }}
                />
                {errors.metallicColor && (
                  <small className="text-danger">{errors.metallicColor}</small>
                )}
              </div>

              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaleFormPage;
